import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";

import {
  Box,
  Container,
  FormControlLabel,
  Grid,
  useTheme,
} from "@mui/material";
import { PagesID } from "@utils/utilsConfigurations";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { MethodType } from "src/services/type";
import { ServicesURL } from "@utils/utilsApi";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import { Association, ColumnsType } from "src/interfaces/Common";
import { CreateLeaderboardCluster, GetLeaderboardClusterData } from "../types";
import { defaultValidation } from "@utils/utilsValidators";
import { ColumnInterface } from "@components/Table/types";
import AssociationTableCst from "@components/AssociationTableCst";
import ButtonCst from "@components/ButtonCst";
import {
  InputCst,
  InputAssociationChipsCst,
  SingleCheckboxCst,
  DataPickerCst,
} from "@components/InputCst";
import { type Dayjs } from "dayjs";
import { MaterialUISwitch } from "../style";

const EditCluster: React.FC<{ page: PagesID }> = ({ page }) => {
  const theme = useTheme();
  const { t } = useTranslation(["leaderboard-cluster-page", "form"]);
  const navigate = useNavigate();
  const { id, initiativeId, clusterId } = useParams();
  const [inputForm, setInputForm] = useState<any>({});
  const [errorInputForm, setErrorInputForm] = React.useState<any>({});
  const userQuery = new URLSearchParams();
  userQuery.append("corporate_id", id!);
  [
    "BUSINESS_ADMIN",
    "PRODUCT_OWNER",
    "TEACHER",
    "HR",
    "MANAGER",
    // "CONTENT_CREATOR",
    "MACHINE",
    "PROVIDER",
    "TUTOR",
    "ROLE_1",
    "ROLE_2",
    "ROLE_3",
    "ROLE_4",
    "ROLE_5",
    "ROLE_6",
    "ROLE_7",
    "ROLE_8",
    "ROLE_9",
    "ROLE_10",
    "ROLE_11",
    "ROLE_12",
    "ROLE_13",
    "ROLE_14",
    "ROLE_15",
    "ROLE_16",
    "ROLE_17",
    "ROLE_18",
    "ROLE_19",
    "ROLE_20",
  ].forEach((role) => {
    userQuery.append("roles", role);
  });

  const [openAssociations, setOpenAssociations] = useState<
    boolean | Association
  >(false);
  const [ownersAssociation, setOwnersAssociation] = useState<
    { id: number; label: string }[]
  >([]);

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();

  const [clusterData, setClusterData] = useState<GetLeaderboardClusterData>();

  const { columns, dataDetail, mutate } = useFetchDataDetail(
    ServicesURL.getLeaderboardCluster
      .replace(":corporateId", id!)
      .replace(":initiativeId", initiativeId!)
      .replace(":clusterId", clusterId!),
    PagesID["corporate.initiative.leaderboard-cluster.add"],
  );

  const columnsByAccessorKey = columns?.reduce<{ [k: string]: ColumnsType }>(
    (agg, cur) => {
      agg[cur.accessorKey] = cur;
      return agg;
    },
    {},
  );

  const onConfirm = () => {
    const { canBeSubmit, showError } = defaultValidation(columns!, inputForm);
    setErrorInputForm(showError);
    let stringError = "";
    const findErrorCol = columns!.map((el: ColumnInterface) => {
      if (el.error) {
        stringError = el.error;
        return {
          [el.accessorKey]: {
            msg: el.error,
          },
        };
      } else {
        return;
      }
    });
    const mergeErrorArray = Object.assign({}, ...findErrorCol);
    if (Object.keys(mergeErrorArray).length > 0) {
      setErrorInputForm({ ...showError, ...mergeErrorArray });
    }
    if (canBeSubmit) {
      onSubmit(inputForm);
    }
  };

  const getErrMsg = (accessorKey: string): string => {
    return errorInputForm[accessorKey]?.msg || errorInputForm[accessorKey];
  };

  const getInitialData = () => {
    let initialState: any = {};
    columns?.forEach(({ accessorKey }) => {
      initialState = {
        ...initialState,
        [accessorKey]: (
          (dataDetail as GetLeaderboardClusterData | undefined)?.cluster as
            | any
            | undefined
        )?.[accessorKey],
      };
    });
    initialState.hasValidity =
      !!(dataDetail as GetLeaderboardClusterData | undefined)?.cluster
        ?.validity_start &&
      !!(dataDetail as GetLeaderboardClusterData | undefined)?.cluster
        ?.validity_end;
    initialState.anonymize = !(
      dataDetail as GetLeaderboardClusterData | undefined
    )?.cluster?.anonymize;
    return initialState;
  };

  useEffect(() => {
    if (dataDetail) {
      setOwnersAssociation(
        ((dataDetail as GetLeaderboardClusterData).owners ?? []).map(
          (owner) => ({
            id: owner.id_user,
            label: `${owner.name ?? ""} ${owner.surname ?? ""}`,
          }),
        ),
      );
      setInputForm(getInitialData());
      setClusterData(dataDetail);
    }
  }, [dataDetail]);

  const setOpenAssociation = (accessorKey: string, value: boolean) => {
    switch (accessorKey) {
      case "owners":
        setOpenAssociations(
          value
            ? {
                accessorKey: "owners",
                mediaType: "users",
                type: "table",
                titleKey: "owner-association",
                pageId: PagesID["users"],
                service: `${ServicesURL.digitedUsers}?${userQuery.toString()}`,
              }
            : value,
        );
        break;
    }
  };

  const getValues = (accessorKey: string) => {
    switch (accessorKey) {
      case "owners":
        return ownersAssociation;
      case "name":
        return clusterData?.cluster.name;
    }
  };

  const handleDelete = (accessorKey: string, e: any) => {
    switch (accessorKey) {
      case "owners":
        setOwnersAssociation(
          ownersAssociation.filter((element: any) => element.id !== e.id),
        );
        break;
    }
  };

  const handleAssociation = (accessorKey: string, value: any) => {
    switch (accessorKey) {
      case "owners":
        const objToTopicsAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return ownersAssociation.find((elem: any) => elem.id === +key);
            }
          },
        );
        setOwnersAssociation(objToTopicsAssociation);
        break;
    }
  };

  const { trigger: triggetUpdateLeaderboardCluster } = useSWRMutation(
    ServicesURL.updateLeaderboardCluster
      .replace(":corporateId", id!)
      .replace(":initiativeId", initiativeId!)
      .replace(":clusterId", clusterId!),
    (url: string, arg: { arg: any }) =>
      fetcher<any>(`${url}`, MethodType.PUT, { ...arg.arg }),
  );

  const onSubmit = async (inputForm: any) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });

    const createBody: CreateLeaderboardCluster = {
      cluster: {
        corporate_id: +id!,
        initiative_id: +initiativeId!,
        anonymize: !inputForm.anonymize,
        description: inputForm.description,
        validity_end: inputForm.validity_end,
        validity_start: inputForm.validity_start,
        is_visible: inputForm.is_visible,
        name: inputForm.name,
      },
      owners: ownersAssociation.map(({ id }) => id),
    };

    try {
      await triggetUpdateLeaderboardCluster(createBody);
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      setOpenToast(undefined);
    }
    mutate && mutate();
  };

  const resetInputForm = () => {
    setInputForm(getInitialData());
  };

  return (
    <>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-community.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={
            openAssociations ? t("owners.hero-title") : t("edit.hero-title")
          }
          description={""}
        />
        <Box
          sx={{
            mt: "25px",
            "&>.MuiGrid-root.MuiGrid-container>.MuiGrid-root.MuiGrid-item.MuiGrid-grid-xs-12>.MuiBox-root":
              {
                justifyContent: "flex-start",
                ".MuiGrid-root.MuiGrid-container:last-child": {
                  marginTop: "auto",
                },
              },
          }}
        >
          {!openAssociations && (
            <Grid container>
              <Grid item xs={12} lg={12} xl={12}>
                <Box
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    padding: "32px",
                    minHeight: "495px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Grid container spacing={3}>
                    <Grid key={"name"} item xs={12} sm={4}>
                      <InputCst
                        key="name"
                        id="name"
                        label={columnsByAccessorKey?.["name"].header}
                        value={inputForm.name}
                        required={true}
                        onChange={(e) => {
                          setInputForm({ ...inputForm, name: e.target.value });
                        }}
                        error={!!errorInputForm["name"]}
                        errorMessage={
                          t(`error.field.${getErrMsg("name")}`) || "Error"
                        }
                        disabled={false}
                        type="text"
                        col={columnsByAccessorKey?.["name"]}
                      />
                    </Grid>
                    <Grid key={"owners"} item xs={12} sm={4}>
                      <InputAssociationChipsCst
                        key={"owners"}
                        id={"owners"}
                        label={columnsByAccessorKey?.["owners"].header}
                        value={getValues("owners")}
                        error={!!errorInputForm["owners"]}
                        errorMessage={
                          t(`error.field.${getErrMsg("owners")}`) || "Error"
                        }
                        required={true}
                        onDelete={(e) => handleDelete("owners", e)}
                        onRedirect={() => setOpenAssociation("owners", true)}
                      />
                    </Grid>
                    <Grid key={"description"} item xs={12} sm={4}>
                      <InputCst
                        key="description"
                        id="description"
                        label={columnsByAccessorKey?.["description"].header}
                        value={inputForm.description}
                        required={true}
                        onChange={(e) => {
                          setInputForm({
                            ...inputForm,
                            description: e.target.value,
                          });
                        }}
                        error={!!errorInputForm["description"]}
                        errorMessage={
                          t(`error.field.${getErrMsg("description")}`) ||
                          "Error"
                        }
                        disabled={false}
                        type="text"
                        col={columnsByAccessorKey?.["description"]}
                      />
                    </Grid>
                    <Grid key={"is_visible"} item xs={12} sm={4}>
                      <SingleCheckboxCst
                        key={"is_visible"}
                        id={"is_visible"}
                        value={inputForm["is_visible"]}
                        label={columnsByAccessorKey?.["is_visible"].header}
                        onChange={(e: any) => {
                          setInputForm({
                            ...inputForm,
                            is_visible: e.target.checked,
                          });
                        }}
                        error={!!errorInputForm["is_visible"]}
                        errorMessage={
                          t(`error.field.${getErrMsg("is_visible")}`) || "Error"
                        }
                      />
                    </Grid>
                    <Grid key={"anonymize"} item xs={12} sm={4}>
                      <SingleCheckboxCst
                        key={"anonymize"}
                        id={"anonymize"}
                        value={inputForm["anonymize"]}
                        label={columnsByAccessorKey?.["anonymize"].header}
                        onChange={(e: any) => {
                          setInputForm({
                            ...inputForm,
                            anonymize: e.target.checked,
                          });
                        }}
                        error={!!errorInputForm["anonymize"]}
                        errorMessage={
                          t(`error.field.${getErrMsg("anonymize")}`) || "Error"
                        }
                      />
                    </Grid>
                    <Grid key={"spacer"} item xs={0} sm={4} />
                    <Grid
                      key={"hasValidity"}
                      item
                      xs={12}
                      sm={4}
                      sx={{
                        alignContent: "center",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <MaterialUISwitch
                            key={"hasValidity"}
                            id={"hasValidity"}
                            value={inputForm["hasValidity"]}
                            checked={inputForm["hasValidity"]}
                            onChange={(e: any) => {
                              setInputForm({
                                ...inputForm,
                                hasValidity: e.target.checked,
                                validity_start: e.target.checked
                                  ? inputForm.validity_start
                                  : undefined,
                                validity_end: e.target.checked
                                  ? inputForm.validity_end
                                  : undefined,
                              });
                            }}
                          />
                        }
                        label={columnsByAccessorKey?.["hasValidity"].header}
                        labelPlacement="start"
                        sx={{
                          "&.MuiFormControlLabel-root": {
                            margin: 0,
                            width: "100%",
                            justifyContent: "space-between",
                          },
                        }}
                      />
                    </Grid>
                    <Grid key={"validity_start"} item xs={12} sm={4}>
                      <DataPickerCst
                        id={"validity_start"}
                        label={columnsByAccessorKey?.["validity_start"].header}
                        value={inputForm["validity_start"]}
                        required={false}
                        onChange={(e: any) => {
                          const newVal = e as Dayjs | undefined;
                          const dateVal = newVal
                            ? new Date(
                                Date.UTC(
                                  newVal.year(),
                                  newVal.month(),
                                  newVal.date(),
                                ),
                              )
                            : undefined;
                          setInputForm({
                            ...inputForm,
                            validity_start: dateVal,
                          });
                        }}
                        error={!!errorInputForm["validity_start"]}
                        errorMessage={
                          t(`error.field.${getErrMsg("validity_start")}`) ||
                          "Error"
                        }
                        disabled={!inputForm["hasValidity"]}
                      />
                    </Grid>
                    <Grid key={"validity_end"} item xs={12} sm={4}>
                      <DataPickerCst
                        id={"validity_end"}
                        label={columnsByAccessorKey?.["validity_end"].header}
                        value={inputForm["validity_end"]}
                        required={false}
                        onChange={(e: any) => {
                          const newVal = e as Dayjs | undefined;
                          const dateVal = newVal
                            ? new Date(
                                Date.UTC(
                                  newVal.year(),
                                  newVal.month(),
                                  newVal.date(),
                                ),
                              )
                            : undefined;
                          setInputForm({ ...inputForm, validity_end: dateVal });
                        }}
                        error={!!errorInputForm["validity_end"]}
                        errorMessage={
                          t(`error.field.${getErrMsg("validity_end")}`) ||
                          "Error"
                        }
                        disabled={!inputForm["hasValidity"]}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={3}>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        mt: "32px",
                        display: "flex",
                        alignItems: "center",
                        gap: "20px",
                        justifyContent: "space-between",
                        [theme.breakpoints.down("sm")]: {
                          flexDirection: "column",
                        },
                      }}
                    >
                      <ButtonCst
                        id={"back-button"}
                        variant={"outlined"}
                        size={"medium"}
                        sx={{
                          minWidth: "150px",
                          mr: "auto",
                          [theme.breakpoints.down("lg")]: {
                            minWidth: "100px",
                          },
                          [theme.breakpoints.down("sm")]: {
                            minWidth: "100%",
                          },
                        }}
                        onClick={() => {
                          navigate(-1);
                        }}
                      >
                        {t("backButton", { ns: "form" })}
                      </ButtonCst>
                      <ButtonCst
                        id={"reset-button"}
                        variant={"outlined"}
                        size={"medium"}
                        sx={{
                          minWidth: "150px",
                          [theme.breakpoints.down("lg")]: {
                            minWidth: "100px",
                          },
                          [theme.breakpoints.down("sm")]: {
                            minWidth: "100%",
                          },
                        }}
                        onClick={resetInputForm}
                      >
                        {t("resetButton", { ns: "form" })}
                      </ButtonCst>
                      <ButtonCst
                        id={"save-button"}
                        variant={"contained"}
                        size={"medium"}
                        sx={{
                          minWidth: "150px",
                          [theme.breakpoints.down("lg")]: {
                            minWidth: "100px",
                          },
                          [theme.breakpoints.down("sm")]: {
                            minWidth: "100%",
                          },
                        }}
                        onClick={() => onConfirm()}
                      >
                        {t("saveButton", { ns: "form" })}
                      </ButtonCst>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          )}
          {openAssociations && (
            <AssociationTableCst
              association={getValues(
                (openAssociations as Association).accessorKey,
              )}
              setAssociation={(value: any) => {
                handleAssociation(
                  (openAssociations as Association).accessorKey,
                  value,
                );
                setInputForm({
                  ...inputForm,
                  ...{ [(openAssociations as Association).accessorKey]: value },
                });
              }}
              setCloseAssociation={(value: boolean) =>
                setOpenAssociation(
                  (openAssociations as Association).accessorKey,
                  value,
                )
              }
              title={t((openAssociations as Association).titleKey, {
                ns: "form",
              })}
              service={(openAssociations as { service: string }).service}
              pageID={(openAssociations as { pageId: PagesID }).pageId}
              filter={(openAssociations as Association).accessorKey}
              defaultFilters={(openAssociations as Association).defaultFilters}
              manageConfigs={(openAssociations as Association).manageConfigs}
              enableMultiRowSelection={
                (openAssociations as Association).multiChoice
              }
              labelKey={(openAssociations as Association).labelKey}
              durationKey={false}
            />
          )}
        </Box>
      </Container>
      <ModalConfirmsCst
        open={!!openToast}
        title={""}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
    </>
  );
};

export default EditCluster;
